import { useCallback, useMemo } from "react";
import { DecodedValueMap, QueryParamConfigMap, SetQuery, useQueryParams } from "use-query-params";

export type SetParam<
  T extends QueryParamConfigMap,
  TParams extends DecodedValueMap<T> = DecodedValueMap<T>,
> = (key: keyof TParams) => (value: TParams[typeof key]) => void;

export type UseQueryParamsState<T extends QueryParamConfigMap> = [
  DecodedValueMap<T>,
  SetParam<T>,
  SetQuery<T>,
];

export const useQueryParamsState = <T extends QueryParamConfigMap>(
  paramConfigMap: T,
): UseQueryParamsState<T> => {
  const [queryParams, setQueryParams] = useQueryParams(paramConfigMap);

  const setQueryParam: SetParam<T> = useCallback(
    (key) => (value) =>
      setQueryParams((prev) => {
        let v = value;
        if (typeof value === "function") {
          v = value(prev[key]);
        }

        return { ...prev, [key]: v };
      }),
    [setQueryParams],
  );

  return useMemo(
    () => [queryParams, setQueryParam, setQueryParams],
    [queryParams, setQueryParam, setQueryParams],
  );
};
